// src/services/medicalProfessionalsService.js - COMPLETE with facility handling and AuthContext integration
import { apiService } from "./api";

export const medicalProfessionalsService = {
  // Get medical professionals with enhanced filtering and facility awareness
  getMedicalProfessionals: async (skip = 0, limit = 100, facilityId = null) => {
    try {
      const params = { skip, limit };
      if (facilityId) {
        params.facility_id = facilityId;
      }

      const response = await apiService.get("/medical-professionals", {
        params,
      });

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching medical professionals:", error);

      // Enhanced error handling
      if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to view medical professionals in this facility"
        );
      } else if (error.response?.status === 404) {
        return []; // No medical professionals found
      }

      throw new Error(
        "Failed to load medical professionals. Please try again."
      );
    }
  },

  getMyRouteOrders: async (status = "assigned") => {
    try {

      // Call API with status parameter
      const response = await apiService.get(
        "/medical-professionals/my-route-orders",
        {
          params: { status },
        }
      );


      // Ensure response has the expected structure
      const data = response.data || {};
      return {
        routes: Array.isArray(data.routes) ? data.routes : [],
        unassigned_orders: Array.isArray(data.unassigned_orders)
          ? data.unassigned_orders
          : [],
        total_orders: data.total_orders || 0,
        total_routes: data.total_routes || 0,
        status_filter: data.status_filter || status,
        medical_professional: data.medical_professional || {},
      };
    } catch (error) {
      console.error("Error fetching MP route orders:", error);
      throw new Error(
        "Unable to load your orders at this time. Please try again."
      );
    }
  },

  getRouteOrdersForDate: async (date) => {
    try {
      const dateStr =
        typeof date === "string" ? date : date.toISOString().split("T")[0];
      return await medicalProfessionalsService.getMyRouteOrders(
        dateStr,
        "assigned"
      );
    } catch (error) {
      console.error("Error fetching route orders for date:", error);
      throw error;
    }
  },

  // Get medical professional by ID with facility validation
  getMedicalProfessionalById: async (mpId) => {
    try {
      const response = await apiService.get(`/medical-professionals/${mpId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching medical professional ${mpId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Medical professional not found");
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to view this medical professional"
        );
      }

      throw new Error("Failed to load medical professional details");
    }
  },

  // Create medical professional (basic)
  createMedicalProfessional: async (mpData) => {
    try {
      const response = await apiService.post("/medical-professionals", mpData);
      return response.data;
    } catch (error) {
      console.error("Error creating medical professional:", error);

      if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("Invalid mp_category")) {
          throw new Error("Invalid medical professional category selected");
        } else if (detail?.includes("facility")) {
          throw new Error("Invalid facility assignment");
        }
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to create medical professionals in this facility"
        );
      }

      throw error;
    }
  },

  // Create custom medical professional with user account - ENHANCED with facility validation
  createCustomMedicalProfessional: async (mpData) => {
    let retryCount = 0;
    const maxRetries = 3;
    const baseDelay = 2000;


    const attemptCreation = async () => {
      try {
        // Enhanced validation with better error messages
        const requiredFields = [
          "full_name",
          "username",
          "email",
          "phone_number",
          "mp_category",
        ];
        const missingFields = requiredFields.filter(
          (field) => !mpData[field]?.trim()
        );

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }

        // Enhanced email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(mpData.email)) {
          throw new Error("Invalid email format");
        }

        if (!mpData.phone_number?.trim()) {
          throw new Error("Phone number is required for SMS verification");
        }

        // Enhanced username validation
        if (mpData.username.length < 3) {
          throw new Error("Username must be at least 3 characters long");
        }

        // Prepare data with correct field mapping - facility_id is handled by backend
        const apiData = {
          full_name: mpData.full_name.trim(),
          username: mpData.username.trim().toLowerCase(),
          email: mpData.email.trim().toLowerCase(),
          password: mpData.password || null,
          phone_number: mpData.phone_number.trim(),
          mp_category: mpData.mp_category.toLowerCase(),

          // Support both field names for license
          medical_license_number:
            mpData.medical_license_number?.trim() ||
            mpData.license_number?.trim() ||
            null,
          license_number:
            mpData.license_number?.trim() ||
            mpData.medical_license_number?.trim() ||
            null,

          npi_number: mpData.npi_number?.trim() || null,

          // FIXED: Only include facility_id if provided (let backend handle facility assignment for facility admins)
          ...(mpData.facility_id && {
            facility_id: parseInt(mpData.facility_id),
          }),

          send_activation_email: mpData.send_activation_email || false,
        };

        // Add starting point if provided
        if (mpData.starting_point) {
          apiData.starting_point = {
            name: mpData.starting_point.name || mpData.starting_point.address,
            address: mpData.starting_point.address,
            type: mpData.starting_point.type || "home",
            ...(mpData.starting_point.lat !== undefined && {
              lat: mpData.starting_point.lat,
            }),
            ...(mpData.starting_point.lng !== undefined && {
              lng: mpData.starting_point.lng,
            }),
          };
        }


        const response = await apiService.post(
          "/medical-professionals/custom",
          apiData
        );


        // Normalize response format for frontend compatibility
        const normalizedResponse = {
          id: response.data.id,
          user_id: response.data.user_id,
          user: response.data.user,
          mp_category: response.data.mp_category,
          medical_license_number: response.data.medical_license_number,
          license_number:
            response.data.license_number ||
            response.data.medical_license_number,
          npi_number: response.data.npi_number,
          facility_id: response.data.facility_id,
          status: "active",
          is_active: true,
          message: response.data.message,
        };

        return normalizedResponse;
      } catch (error) {
        console.error(
          `Error creating medical professional (attempt ${retryCount + 1}):`,
          error
        );

        // Enhanced error handling for facility-related issues
        if (error.response?.status === 400) {
          const detail = error.response.data?.detail;
          if (
            detail?.includes("Email already registered") ||
            detail?.includes("email")
          ) {
            throw new Error("This email address is already registered");
          } else if (
            detail?.includes("Username already taken") ||
            detail?.includes("username")
          ) {
            throw new Error("This username is already taken");
          } else if (
            detail?.includes("facility") ||
            detail?.includes("Facility")
          ) {
            throw new Error(
              "Invalid facility assignment or facility not found"
            );
          } else if (detail?.includes("Invalid mp_category")) {
            throw new Error(
              `Invalid medical professional category: ${mpData.mp_category}`
            );
          } else if (detail?.includes("Password required")) {
            throw new Error(
              "Password is required when not sending activation email"
            );
          } else if (detail?.includes("phone number")) {
            throw new Error(
              "Valid phone number is required for SMS verification"
            );
          } else {
            throw new Error(`Invalid data: ${detail}`);
          }
        } else if (error.response?.status === 403) {
          const detail = error.response.data?.detail;
          if (detail?.includes("facility") || detail?.includes("Facility")) {
            throw new Error(
              "You don't have permission to create medical professionals in this facility"
            );
          } else {
            throw new Error(
              "You don't have permission to create medical professionals"
            );
          }
        } else if (error.response?.status === 409) {
          const detail = error.response.data?.detail;
          if (detail?.includes("Email")) {
            throw new Error("This email address is already registered");
          } else if (detail?.includes("Username")) {
            throw new Error("This username is already taken");
          } else {
            throw new Error(
              "A medical professional with this information already exists"
            );
          }
        } else if (error.response?.status === 422) {
          const detail = error.response.data?.detail;
          if (Array.isArray(detail)) {
            const errorMessages = detail
              .map((err) => err.msg || err.message)
              .join(", ");
            throw new Error(`Validation error: ${errorMessages}`);
          } else {
            throw new Error(
              `Validation error: ${detail || "Please check your input data"}`
            );
          }
        }

        // Circuit breaker handling
        if (
          error.message?.includes("Circuit breaker") ||
          error.message?.includes("circuit breaker") ||
          error.response?.status === 503
        ) {
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = baseDelay * Math.pow(2, retryCount - 1);

            await new Promise((resolve) => setTimeout(resolve, delay));
            return attemptCreation();
          } else {
            throw new Error(
              "Service temporarily unavailable due to high load. Please wait a few minutes and try again."
            );
          }
        }

        // Retry logic for server errors and network issues
        if (error.response?.status >= 500 || !error.response) {
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = baseDelay * retryCount;

            await new Promise((resolve) => setTimeout(resolve, delay));
            return attemptCreation();
          } else {
            throw new Error(
              error.response
                ? "Server error occurred. Please try again later."
                : "Network error. Please check your connection and try again."
            );
          }
        }

        // Re-throw the original error if no specific handling applies
        throw error;
      }
    };

    return attemptCreation();
  },

  // FIXED: Update medical professional method with proper facility validation
  updateMedicalProfessional: async (mpId, updateData) => {
    try {

      const response = await apiService.put(
        `/medical-professionals/${mpId}`,
        updateData
      );

      return response.data;
    } catch (error) {
      console.error(`Error updating medical professional ${mpId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Medical professional not found");
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to update this medical professional"
        );
      } else if (error.response?.status === 405) {
        throw new Error(
          "Update operation not supported. Please contact support."
        );
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        throw new Error(`Invalid data: ${detail || "Please check your input"}`);
      }

      throw error;
    }
  },

  // Delete medical professional with facility validation
  deleteMedicalProfessional: async (mpId) => {
    try {
      const response = await apiService.delete(
        `/medical-professionals/${mpId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting medical professional ${mpId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Medical professional not found");
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to delete this medical professional"
        );
      } else if (error.response?.status === 409) {
        throw new Error(
          "Cannot delete medical professional with active assignments"
        );
      }

      throw error;
    }
  },

  // Send activation email
  sendActivationEmail: async (mpId) => {
    try {
      const response = await apiService.post(
        `/medical-professionals/${mpId}/send-activation`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error sending activation email to medical professional ${mpId}:`,
        error
      );

      if (error.response?.status === 404) {
        throw new Error("Medical professional not found");
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("already active")) {
          throw new Error("Medical professional account is already active");
        } else {
          throw new Error("Unable to send activation email");
        }
      }

      throw error;
    }
  },

  // Starting point management methods
  updateStartingPoint: async (mpId, startingPointData) => {
    try {

      const response = await apiService.put(
        `/medical-professionals/${mpId}/starting-point`,
        startingPointData
      );

      return response.data;
    } catch (error) {
      console.error(`Error updating starting point for MP ${mpId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Medical professional not found");
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to update this medical professional"
        );
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        throw new Error(
          `Invalid starting point data: ${detail || "Please check your input"}`
        );
      }

      throw error;
    }
  },

  getStartingPoint: async (mpId) => {
    try {
      const response = await apiService.get(
        `/medical-professionals/${mpId}/starting-point`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching starting point for MP ${mpId}:`, error);

      if (error.response?.status === 404) {
        return null; // No starting point configured
      } else if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to view this starting point"
        );
      }

      throw error;
    }
  },

  deleteStartingPoint: async (mpId) => {
    try {
      const response = await apiService.delete(
        `/medical-professionals/${mpId}/starting-point`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting starting point for MP ${mpId}:`, error);

      if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to modify this medical professional"
        );
      }

      throw error;
    }
  },

  getMedicalProfessionalsWithStartingPoints: async () => {
    try {
      const response = await apiService.get(
        "/medical-professionals/starting-points"
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching medical professionals with starting points:",
        error
      );

      if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to view medical professionals"
        );
      }

      throw error;
    }
  },

  // Get available medical professionals for assignment (facility-aware)
  getAvailableMedicalProfessionals: async (date, facilityId = null) => {
    try {
      const params = {};
      if (date) params.date = date;
      if (facilityId) params.facility_id = facilityId;

      const response = await apiService.get(
        "/medical-professionals/available",
        { params }
      );
      return response.data;
    } catch (error) {
      console.warn(
        "Error fetching available medical professionals:",
        error.message
      );

      // Fallback: return all medical professionals for the facility
      return await medicalProfessionalsService.getMedicalProfessionals(
        0,
        100,
        facilityId
      );
    }
  },

  // Search medical professionals (facility-aware)
  searchMedicalProfessionals: async (searchQuery, limit = 50) => {
    try {
      const params = { q: searchQuery, limit };
      const response = await apiService.get("/medical-professionals/search", {
        params,
      });
      return response.data;
    } catch (error) {
      console.warn("Error searching medical professionals:", error.message);

      // Fallback to regular getMedicalProfessionals with filter
      const allMps = await medicalProfessionalsService.getMedicalProfessionals(
        0,
        limit
      );
      const searchTerm = searchQuery.toLowerCase();

      return allMps.filter(
        (mp) =>
          mp.user?.full_name?.toLowerCase().includes(searchTerm) ||
          mp.mp_category?.toLowerCase().includes(searchTerm) ||
          mp.medical_license_number?.toLowerCase().includes(searchTerm) ||
          mp.license_number?.toLowerCase().includes(searchTerm) ||
          mp.npi_number?.toLowerCase().includes(searchTerm)
      );
    }
  },

  // Get medical professional categories
  getMedicalProfessionalCategories: async () => {
    try {
      const response = await apiService.get(
        "/medical-professionals/categories"
      );
      return response.data;
    } catch (error) {
      console.warn("Error fetching categories, using fallback:", error.message);

      // Fallback categories
      return [
        { value: "nurse", label: "Nurse" },
        { value: "doctor", label: "Doctor" },
        { value: "technician", label: "Technician" },
        { value: "phlebotomist", label: "Phlebotomist" },
        { value: "other", label: "Other" },
      ];
    }
  },

  // ENHANCED: Validation utilities for better frontend integration
  validateMedicalProfessionalData: (mpData) => {
    const errors = {};

    // Required fields validation
    if (!mpData.full_name?.trim()) {
      errors.full_name = "Full name is required";
    } else if (mpData.full_name.trim().length < 2) {
      errors.full_name = "Full name must be at least 2 characters";
    }

    if (!mpData.username?.trim()) {
      errors.username = "Username is required";
    } else if (mpData.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!mpData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mpData.email)) {
      errors.email = "Invalid email format";
    }

    if (!mpData.phone_number?.trim()) {
      errors.phone_number = "Phone number is required for SMS verification";
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(mpData.phone_number.trim())) {
      errors.phone_number = "Please enter a valid phone number";
    }

    if (!mpData.mp_category) {
      errors.mp_category = "Medical professional category is required";
    }

    // Note: facility_id validation is removed since backend handles it for facility admins

    // Conditional validation
    if (!mpData.password && !mpData.send_activation_email) {
      errors.password =
        "Password is required when not sending activation email";
    } else if (mpData.password && mpData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    // License validation (optional but if provided should be valid)
    if (
      mpData.medical_license_number &&
      mpData.medical_license_number.trim().length < 3
    ) {
      errors.medical_license_number =
        "Medical license number must be at least 3 characters";
    }

    if (mpData.npi_number && !/^\d{10}$/.test(mpData.npi_number.trim())) {
      errors.npi_number = "NPI number must be exactly 10 digits";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Validate starting point data
  validateStartingPointData: (startingPointData) => {
    const errors = {};

    if (!startingPointData.address?.trim()) {
      errors.address = "Address is required";
    }

    if (startingPointData.lat !== undefined && startingPointData.lat !== null) {
      if (startingPointData.lat < -90 || startingPointData.lat > 90) {
        errors.lat = "Latitude must be between -90 and 90";
      }
    }

    if (startingPointData.lng !== undefined && startingPointData.lng !== null) {
      if (startingPointData.lng < -180 || startingPointData.lng > 180) {
        errors.lng = "Longitude must be between -180 and 180";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Format medical professional for display
  formatMedicalProfessional: (mp) => {
    if (!mp) return null;

    return {
      id: mp.id,
      name: mp.user?.full_name || `MP ${mp.id}`,
      email: mp.user?.email || "",
      category: mp.mp_category || "unknown",
      license: mp.medical_license_number || mp.license_number || "",
      npi: mp.npi_number || "",
      facility_id: mp.facility_id,
      status: mp.status || (mp.is_active ? "active" : "inactive"),
      display_name: `${mp.user?.full_name || `MP ${mp.id}`} (${
        mp.mp_category || "unknown"
      })`,
    };
  },

  // Enhanced facility validation utilities
  validateFacilityAccess: (userRole, userFacilityId, targetFacilityId) => {
    // Super admin can access any facility
    if (userRole === "super_admin") {
      return { hasAccess: true, reason: "Super admin access" };
    }

    // Facility admin and admin can only access their own facility
    if (userRole === "facility_admin" || userRole === "admin") {
      if (!userFacilityId) {
        return {
          hasAccess: false,
          reason: "No facility assigned to your account",
        };
      }

      if (targetFacilityId && targetFacilityId !== userFacilityId) {
        return {
          hasAccess: false,
          reason: "Can only access your own facility",
        };
      }

      return { hasAccess: true, reason: "Facility access granted" };
    }

    // Medical professionals can only access their own records
    if (userRole === "medical_professional") {
      return {
        hasAccess: false,
        reason:
          "Medical professionals cannot manage other medical professionals",
      };
    }

    return { hasAccess: false, reason: "Insufficient permissions" };
  },

  // Get user's accessible facilities
  getAccessibleFacilities: async () => {
    try {
      const response = await apiService.get("/facilities");
      return response.data;
    } catch (error) {
      console.error("Error fetching accessible facilities:", error);

      if (error.response?.status === 403) {
        return []; // User has no facility access
      }

      throw new Error("Failed to load facilities");
    }
  },

  // Circuit breaker utilities
  isCircuitBreakerError: (error) => {
    return (
      error.message?.includes("Circuit breaker") ||
      error.message?.includes("circuit breaker") ||
      error.response?.status === 503
    );
  },

  // Get circuit breaker status (if available)
  getCircuitBreakerStatus: async () => {
    try {
      const response = await apiService.get("/admin/circuit-breaker-status");
      return response.data;
    } catch (error) {
      console.warn("Circuit breaker status not available:", error.message);
      return { status: "unknown", error: error.message };
    }
  },

  // Reset circuit breaker (if available)
  resetCircuitBreaker: async () => {
    try {
      const response = await apiService.post("/admin/reset-circuit-breaker");
      return response.data;
    } catch (error) {
      console.error("Failed to reset circuit breaker:", error);
      throw new Error(
        "Failed to reset circuit breaker: " +
          (error.response?.data?.detail || error.message)
      );
    }
  },

  // ADDED: Helper function to check if user can create medical professionals
  canCreateMedicalProfessionals: (user) => {
    return (
      user && ["super_admin", "facility_admin", "admin"].includes(user.role)
    );
  },

  // ADDED: Helper function to check if user can edit medical professionals
  canEditMedicalProfessionals: (user) => {
    return (
      user && ["super_admin", "facility_admin", "admin"].includes(user.role)
    );
  },

  // ADDED: Helper function to get facility-specific error messages
  getFacilityErrorMessage: (error, operation = "access") => {
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail;
      if (detail?.includes("facility")) {
        return `You don't have permission to ${operation} medical professionals in this facility`;
      }
      return `You don't have permission to ${operation} this medical professional`;
    } else if (error.response?.status === 400) {
      const detail = error.response.data?.detail;
      if (detail?.includes("facility")) {
        return "Invalid facility assignment or facility not found";
      }
    }

    return error.message || `Failed to ${operation} medical professional`;
  },

  // ADDED: Check if user has facility access
  hasUserFacilityAccess: (user, targetFacilityId = null) => {
    if (!user) return false;

    if (user.role === "super_admin") return true;

    if (user.role === "facility_admin" || user.role === "admin") {
      if (!user.facility_id) return false;
      if (targetFacilityId) {
        return user.facility_id === targetFacilityId;
      }
      return true;
    }

    return false;
  },

  // ADDED: Get user's facility ID for API calls
  getUserFacilityId: (user) => {
    if (!user) return null;
    return user.facility_id || null;
  },

  // ADDED: Check if user can manage specific medical professional
  canManageMedicalProfessional: (user, medicalProfessional) => {
    if (!user || !medicalProfessional) return false;

    if (user.role === "super_admin") return true;

    if (user.role === "facility_admin" || user.role === "admin") {
      return user.facility_id === medicalProfessional.facility_id;
    }

    if (user.role === "medical_professional") {
      return user.id === medicalProfessional.user_id;
    }

    return false;
  },

  // ADDED: Facility-aware API call wrapper
  makeAuthorizedCall: async (method, endpoint, data = null, params = null) => {
    try {
      const config = {};
      if (params) config.params = params;
      if (data) config.data = data;

      let response;
      switch (method.toLowerCase()) {
        case "get":
          response = await apiService.get(endpoint, config);
          break;
        case "post":
          response = await apiService.post(endpoint, data, config);
          break;
        case "put":
          response = await apiService.put(endpoint, data, config);
          break;
        case "delete":
          response = await apiService.delete(endpoint, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with facility context
      if (error.response?.status === 403) {
        throw new Error(
          "You don't have permission to perform this action in this facility"
        );
      } else if (error.response?.status === 404) {
        throw new Error("Resource not found or you don't have access to it");
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        throw new Error(detail || "Invalid request data");
      }

      throw error;
    }
  },

  // ADDED: Batch operations with facility validation
  batchUpdateMedicalProfessionals: async (updates) => {
    const results = {
      successful: 0,
      failed: 0,
      errors: [],
    };

    for (const update of updates) {
      try {
        await medicalProfessionalsService.updateMedicalProfessional(
          update.id,
          update.data
        );
        results.successful++;
      } catch (error) {
        results.failed++;
        results.errors.push({
          id: update.id,
          error: error.message,
        });
      }
    }

    return results;
  },

  // ADDED: Export medical professionals data
  exportMedicalProfessionals: async (format = "csv", filters = {}) => {
    try {
      const params = { format, ...filters };
      const response = await apiService.get("/medical-professionals/export", {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      console.error("Error exporting medical professionals:", error);
      throw new Error("Failed to export medical professionals data");
    }
  },

  // ADDED: Import medical professionals data
  importMedicalProfessionals: async (file, options = {}) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      Object.keys(options).forEach((key) => {
        formData.append(key, options[key]);
      });

      const response = await apiService.post(
        "/medical-professionals/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error importing medical professionals:", error);
      throw new Error("Failed to import medical professionals data");
    }
  },
};
